import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocsFromServer, query, Timestamp, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { addDaysToDateISO, todayUKDateISO, ukDateISOToUTCStart } from '../../../../utils/ukDates';
import { formatSailingBoatMoney, grossUpSailingBoatPrice, sailingBoatPriceMinor } from '../../../teams/utils/sailingBoatPayments';

const ACTIVE_BOOKING_STATUSES = new Set(['reserved', 'allocated', 'confirmed', 'active']);

function hhmmToMinutes(value) {
  const [hours, minutes] = String(value || '00:00').split(':').map(Number);
  return Number(hours || 0) * 60 + Number(minutes || 0);
}

function minutesToHHMM(value) {
  const bounded = Math.max(0, Math.min(Number(value || 0), 23 * 60 + 59));
  return `${String(Math.floor(bounded / 60)).padStart(2, '0')}:${String(bounded % 60).padStart(2, '0')}`;
}

function overlaps(leftFrom, leftTo, rightFrom, rightTo) {
  const leftStart = hhmmToMinutes(leftFrom);
  const leftEnd = hhmmToMinutes(leftTo);
  const rightStart = hhmmToMinutes(rightFrom);
  const rightEnd = hhmmToMinutes(rightTo);
  return !(leftEnd <= rightStart || rightEnd <= leftStart);
}

function normalizeMemberTags(rawTags) {
  if (Array.isArray(rawTags)) return rawTags.filter(Boolean);
  if (rawTags && typeof rawTags === 'object') {
    return Object.entries(rawTags).filter(([, enabled]) => enabled).map(([name]) => name);
  }
  return [];
}

function boatLabel(boat = {}) {
  return [boat.name || 'Club boat', boat.type || boat.className || '', boat.sailNumber ? `#${boat.sailNumber}` : ''].filter(Boolean).join(' · ');
}

export default function RequestSailingBoatForm({
  isOpen,
  onClose,
  onSave,
  teamId,
  memberId,
  userTags = [],
}) {
  const authUid = getAuth().currentUser?.uid || null;
  const [date, setDate] = useState(todayUKDateISO());
  const [fromTime, setFromTime] = useState('09:00');
  const [durationMin, setDurationMin] = useState(120);
  const [boats, setBoats] = useState([]);
  const [availableIds, setAvailableIds] = useState(null);
  const [preferences, setPreferences] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [feeHandling, setFeeHandling] = useState('deduct');

  const toTime = useMemo(() => minutesToHHMM(hhmmToMinutes(fromTime) + Number(durationMin || 0)), [durationMin, fromTime]);
  const crossesMidnight = hhmmToMinutes(fromTime) + Number(durationMin || 0) > 24 * 60;

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    setLoading(true);
    setPreferences([]);
    setError('');
    getDoc(doc(db, 'teams', teamId)).then((teamSnap) => {
      setFeeHandling(teamSnap.data()?.sailing?.rules?.boatPaymentFeeHandling === 'add' ? 'add' : 'deduct');
    }).catch(() => {});
    getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')).then((setupSnap) => {
      setCurrency(String(setupSnap.data()?.currency || 'GBP').toUpperCase());
    }).catch(() => {});
    return safeOnSnapshot(collection(db, 'teams', teamId, 'sailingBoats'), (snap) => {
      setBoats(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setLoading(false);
    }, (err) => {
      setError(err?.message || 'Could not load club boats.');
      setLoading(false);
    }, { label: 'sailing-request-boat-fleet', teamId });
  }, [isOpen, teamId]);

  const memberTagSet = useMemo(() => new Set(normalizeMemberTags(userTags)), [userTags]);
  const eligibleBoats = useMemo(() => boats.filter((boat) => {
    if (String(boat.status || 'active') !== 'active' || boat.visibility === 'admins') return false;
    const required = Array.isArray(boat.requiredTags) ? boat.requiredTags : Array.isArray(boat.tagsAllowed) ? boat.tagsAllowed : [];
    return required.length === 0 || required.some((tag) => memberTagSet.has(tag));
  }), [boats, memberTagSet]);

  useEffect(() => {
    if (!isOpen || !teamId || !date || !fromTime || !toTime || eligibleBoats.length === 0) {
      setAvailableIds(new Set());
      return undefined;
    }
    let cancelled = false;
    setChecking(true);
    setAvailabilityError('');
    const dayStart = Timestamp.fromDate(ukDateISOToUTCStart(date));
    const dayEnd = Timestamp.fromDate(ukDateISOToUTCStart(addDaysToDateISO(date, 1)));
    (async () => {
      try {
        const checks = await Promise.all(eligibleBoats.map(async (boat) => {
          const snap = await getDocsFromServer(query(
            collection(db, 'teams', teamId, 'sailingBoats', boat.id, 'bookings'),
            where('date', '>=', dayStart),
            where('date', '<', dayEnd)
          ));
          const taken = snap.docs.some((docSnap) => {
            const booking = docSnap.data() || {};
            if (!ACTIVE_BOOKING_STATUSES.has(String(booking.status || 'reserved'))) return false;
            return overlaps(fromTime, toTime, booking.fromTime, booking.toTime);
          });
          return { boatId: boat.id, available: !taken };
        }));
        if (!cancelled) setAvailableIds(new Set(checks.filter((row) => row.available).map((row) => row.boatId)));
      } catch (err) {
        if (!cancelled) {
          setAvailableIds(new Set(eligibleBoats.map((boat) => boat.id)));
          setAvailabilityError('Could not check live availability. Showing eligible active boats.');
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date, eligibleBoats, fromTime, isOpen, teamId, toTime]);

  const visibleBoats = useMemo(() => {
    const available = availableIds || new Set(eligibleBoats.map((boat) => boat.id));
    return eligibleBoats.filter((boat) => available.has(boat.id)).sort((a, b) => boatLabel(a).localeCompare(boatLabel(b), undefined, { sensitivity: 'base' }));
  }, [availableIds, eligibleBoats]);

  const quotedPrices = useMemo(() => new Map(visibleBoats.map((boat) => {
    const base = sailingBoatPriceMinor(boat.payment, Number(durationMin || 0), userTags);
    return [boat.id, {
      base,
      checkout: feeHandling === 'add' ? grossUpSailingBoatPrice(base, currency) : base,
      timing: boat.payment?.collectionTiming || 'at_booking',
    }];
  })), [currency, durationMin, feeHandling, userTags, visibleBoats]);
  const selectedQuote = useMemo(() => preferences.reduce((highest, boatId) => {
    const quote = quotedPrices.get(boatId);
    return Number(quote?.checkout || 0) > Number(highest?.checkout || 0) ? quote : highest;
  }, null), [preferences, quotedPrices]);

  useEffect(() => {
    const visible = new Set(visibleBoats.map((boat) => boat.id));
    setPreferences((current) => current.filter((id) => visible.has(id)));
  }, [visibleBoats]);

  const togglePreference = (boatId) => {
    setPreferences((current) => current.includes(boatId) ? current.filter((id) => id !== boatId) : [...current, boatId]);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (!teamId || !memberId || (authUid && authUid !== memberId)) {
      setError('Missing member information.');
      return;
    }
    if (preferences.length === 0) {
      setError('Choose at least one club boat.');
      return;
    }
    if (crossesMidnight) {
      setError('Choose a duration that ends before midnight.');
      return;
    }
    setSubmitting(true);
    try {
      const submitRequest = httpsCallable(functions, 'submitSailingBoatRequest');
      const result = await submitRequest({
        teamId,
        date,
        fromTime,
        toTime,
        preferences,
        notes: notes.trim() || null,
      });
      if (result.data?.url) {
        window.location.assign(result.data.url);
        return;
      }
      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Could not submit the boat request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <form className="modal-form" onSubmit={submit}>
      <h2>Request a Club Boat</h2>
      <p className="text-muted">Pick a session and rank the club boats you would be happy to use.</p>

      <section className="form-section boat-request-panel">
        <div className="boat-request-grid">
          <label className="form-label">Date<input className="input" type="date" min={todayUKDateISO()} value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label className="form-label">Start<input className="input" type="time" value={fromTime} onChange={(event) => setFromTime(event.target.value)} /></label>
          <label className="form-label">Duration
            <span className="boat-request-duration-control">
              <input className="input" type="number" min="15" step="15" value={durationMin} onChange={(event) => setDurationMin(event.target.value)} aria-label="Duration in minutes" />
              <span>minutes</span>
            </span>
          </label>
          <div className="form-label boat-request-end-time"><span>Ends</span><strong>{toTime}</strong></div>
        </div>
      </section>

      <section className="form-section boat-request-panel">
        <h3>Available boats</h3>
        {loading || checking ? <p className="text-muted">Checking availability…</p> : null}
        {availabilityError ? <p className="boat-request-availability-note text-muted">{availabilityError}</p> : null}
        {!loading && visibleBoats.length === 0 ? <p className="text-muted">No club boats are available for that slot.</p> : null}
        <div style={{ display: 'grid', gap: 8 }}>
          {visibleBoats.map((boat) => (
            <label key={boat.id} className="card" style={{ padding: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
              <input type="checkbox" checked={preferences.includes(boat.id)} onChange={() => togglePreference(boat.id)} />
              <span><strong>{boatLabel(boat)}</strong><br /><span className="text-muted">{[boat.length ? `${boat.length}m` : '', boat.capacity ? `${boat.capacity} people` : '', quotedPrices.get(boat.id)?.base > 0 ? `${formatSailingBoatMoney(quotedPrices.get(boat.id).base, currency)} · ${String(quotedPrices.get(boat.id).timing).replaceAll('_', ' ')}` : 'No charge'].filter(Boolean).join(' · ')}</span></span>
            </label>
          ))}
        </div>
        {selectedQuote?.checkout > 0 && (
          <div className="boat-payment-quote" role="status">
            <strong>Highest selected charge: {formatSailingBoatMoney(selectedQuote.checkout, currency)}</strong>
            <span>If payment is taken now, this is the checkout total. A cheaper allocation is refunded automatically.</span>
          </div>
        )}
      </section>

      <label className="form-label">Notes<textarea className="input" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
      {error && <p className="text-danger">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn btn--secondary" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn--primary" disabled={submitting || loading}>{submitting ? 'Submitting…' : 'Request boat'}</button>
      </div>
    </form>
  );
}
